<template>
  <div class="mt-3">
    <h4>Total {{ total_items }}</h4>
    <div style="display: flex;justify-content: end;">
      <v-btn
        v-if="checkedRows.length > 0"
        color="red"
        dark
        class="mb-3"
        @click="showDeleteDialog = true"
      >
        <v-icon left>delete</v-icon> ({{ checkedRows.length }})
      </v-btn>
    </div>
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete <b>{{ checkedRows.length }}</b> selected Festival(s)?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="red" dark @click="deleteSelectedFestivals">
            Yes, Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table
      no-results-text="Loading... Please wait"
      :headers="headers"
      :items="festival_list"
      :pagination.sync="pagination"
      :loading="loaders.festival_list_loader"
      :total-items="total_items"
      :rows-per-page-items="[10, 20, 50, 100]"
      class="elevation-1"
    >
      <template v-slot:headers="props">
        <tr>
          <th>
            <v-checkbox
              hide-details
              :input-value="allSelected"
              @change="toggleAll"
              :disabled="selectableRows.length === 0"
            ></v-checkbox>
          </th>

          <th v-for="header in props.headers" :key="header.text">
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template v-slot:items="props">
        <tr :key="String(props.item.id)">
          <td style="display:flex; align-items:center; gap:8px;">
            <v-checkbox
              v-model="checkedRows"
              :value="String(props.item.id)"
              :disabled="props.item.status == 0 || !props.item.id"
              hide-details
            ></v-checkbox>
          </td>
          <td>
            <v-btn
              small
              icon
              color="primary"
              @click="copyFestival(props.item)"
              title="Edit Festival"
            >
              <v-icon small>edit</v-icon>
            </v-btn>
          </td>
          <td>{{ props.item.name || '-' }}</td>
          <td>{{ props.item.festival_name || '-' }}</td>
          <td>{{ props.item.address_short || props.item.address || '-' }}</td>
          <td>{{ getLocalStationNames(props.item) }}</td>
          <td>{{ getMetroStationNames(props.item) }}</td>
          <td>{{ getAdditionalInfoValue(props.item, 'visarjan_date_time') }}</td>
          <td>{{ getAdditionalInfoValue(props.item, 'aarti_time') }}</td>
          <td>{{ getAdditionalInfoValue(props.item, 'expected_time') }}</td>
            <a
              v-if="props.item.thumbnail_image"
              :href="props.item.thumbnail_image"
              target="_blank"
              rel="noopener"
            >
              <img
                :src="props.item.thumbnail_image"
                alt="thumbnail"
                style="width: 48px; height: 48px; object-fit: cover; border-radius: 6px;"
              />
            </a>
            <span v-else>-</span>
          </td>
        </tr>
      </template>

      <template v-slot:no-data>
        <v-layout v-if="loaders.festival_list_loader" justify-center>
          Loading... Please wait
        </v-layout>
        <v-layout v-else justify-center>
          No data available
        </v-layout>
      </template>
    </v-data-table>

    <v-snackbar
      :color="snackBar.color"
      :top="snackBar.top"
      :bottom="snackBar.bottom"
      :left="snackBar.left"
      :right="snackBar.right"
      v-model="snackBar.show"
    >
      {{ snackBar.message }}
      <v-btn dark flat @click.native="snackBar.show = false">
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    reload: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      festival_list: [],
      total_items: 0,

      pagination: {
        page: 1,
        rowsPerPage: 10,
        sortBy: "",
        descending: true,
      },

      checkedRows: [],
      showDeleteDialog: false,

      loaders: {
        festival_list_loader: false,
      },

      snackBar: {
        show: false,
        message: "",
        color: "",
        top: true,
        right: true,
      },

      headers: [
        { text: "Action", value: "action", sortable: false },
        { text: "Name", value: "name" },
        { text: "Festival Name", value: "festival_name" },
        { text: "Address", value: "address_short" },
        { text: "Nearby Local Stations", value: "nearby_local_stations" },
        { text: "Nearby Metro Stations", value: "nearby_metro_stations" },
        { text: "Visarjan Date", value: "visarjan_date_time" },
        { text: "Aarti Time", value: "aarti_time" },
        { text: "Expected Time", value: "expected_time" },
        { text: "Thumbnail", value: "thumbnail_image", sortable: false },
      ],
    };
  },
  computed: {
    selectableRows() {
      return this.festival_list.filter(item => item && item.id);
    },
    allSelected() {
      const selectableRows = this.selectableRows;

      return (
        selectableRows.length > 0 &&
        this.checkedRows.length === selectableRows.length
      );
    },
    headersWithCheckbox() {
      return this.headers.filter(h => !h.hidden);
    },
  },

  watch: {
    pagination: {
      handler() {
        this.getFestivalList();
      },
      deep: true,
    },
    reload() {
      this.getFestivalList();
    },
  },

  mounted() {
    this.getFestivalList();
  },

  methods: {
    getFestivalList() {
      this.loaders.festival_list_loader = true;

      axios
        .post(
          `${process.env.VUE_APP_API_URL}/api/v4/get-festival-data-web`,
          {},
          {
            headers: this.$headers,
            params: {
              page: this.pagination.page,
              rowsPerPage: this.pagination.rowsPerPage,
              sortBy: this.pagination.sortBy,
              descending: this.pagination.descending,
            },
          }
        )
        .then((response) => {
          const payload = response.data || {};
          if (payload.status === true || payload.status === 1) {
            const apiData = payload.data;
            const paginatedData = apiData && typeof apiData === "object" && !Array.isArray(apiData)
              ? apiData
              : null;

            const list = Array.isArray(apiData)
              ? apiData
              : Array.isArray(paginatedData && paginatedData.data)
                ? paginatedData.data
                : [];

            this.festival_list = list.map((item) => this.normalizeFestivalItem(item));

            const availableIds = new Set(
              this.festival_list
                .filter((item) => item && item.id)
                .map((item) => String(item.id))
            );

            this.checkedRows = this.checkedRows.filter((id) => availableIds.has(String(id)));

            this.total_items =
              paginatedData && paginatedData.total !== undefined
                ? Number(paginatedData.total)
                : payload.total !== undefined
                  ? Number(payload.total)
                  : this.festival_list.length;

            if (
              paginatedData &&
              paginatedData.current_page &&
              Number(paginatedData.current_page) !== this.pagination.page
            ) {
              this.pagination.page = Number(paginatedData.current_page);
            }
          } else {
            this.festival_list = [];
            this.total_items = 0;
            this.checkedRows = [];
          }
        })
        .catch(() => {
          this.$toast && this.$toast.error("Something went wrong!");
        })
        .finally(() => {
          this.loaders.festival_list_loader = false;
        });
    },
    toggleAll() {
      if (this.allSelected) {
        this.checkedRows = [];
      } else {
        // Select only active rows (status = 1)
        this.checkedRows = this.festival_list
          .filter(item => item.id)
          .map(item => String(item.id));
      }
    },
    normalizeFestivalItem(item) {
      const festivalId =
        item.festival_id ||
        item.festivalId ||
        item.festival_uuid ||
        item.id ||
        item._id ||
        item.uuid ||
        null;

      return {
        ...item,
        id: festivalId,
        edit_festival_id: festivalId,
      };
    },

    parseJsonValue(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === "object") return value;

      if (typeof value === "string") {
        try {
          return JSON.parse(value);
        } catch (e) {
          return value;
        }
      }

      return value;
    },

    getAdditionalInfoValue(item, key) {
      const info = this.parseJsonValue(item.additional_info);
      if (!info || typeof info !== "object") return "-";
      const value = info[key];
      return value === null || value === undefined || value === "" ? "-" : value;
    },

    formatStationValue(value) {
      const parsed = this.parseJsonValue(value);

      if (Array.isArray(parsed)) {
        return parsed.length ? parsed.join(", ") : "-";
      }

      if (parsed && typeof parsed === "object") {
        const values = Object.values(parsed).filter(Boolean);
        return values.length ? values.join(", ") : "-";
      }

      if (typeof parsed === "string") {
        return parsed.trim() || "-";
      }

      return "-";
    },

    getLocalStationNames(item) {
      if (item.nearby_local_stations) {
        return this.formatStationValue(item.nearby_local_stations);
      }

      const nearbyStations = this.parseJsonValue(item.nearby_stations);
      return this.formatStationValue(nearbyStations && nearbyStations.local);
    },

    getMetroStationNames(item) {
      if (Array.isArray(item.nearby_metro_stations) && item.nearby_metro_stations.length) {
        return item.nearby_metro_stations
          .map((station) => station && station.name)
          .filter(Boolean)
          .join(", ") || "-";
      }

      if (item.nearby_metro_stations) {
        return this.formatStationValue(item.nearby_metro_stations);
      }

      const nearbyStations = this.parseJsonValue(item.nearby_stations);
      return this.formatStationValue(nearbyStations && nearbyStations.metro);
    },

    deleteSelectedFestivals() {
      this.showDeleteDialog = false;

      if (this.checkedRows.length === 0) return;

      this.loaders.festival_list_loader = true;

      axios.post(
        `${process.env.VUE_APP_API_URL}/api/v4/delete-festival-data`,
        { ids: this.checkedRows },
        { headers: this.$headers }
      )
      .then((response) => {
        if (response.data.status === true) {
          this.snackBar.message = response.data.message;
          this.snackBar.show = true;
          // alert(response.data.message || "festivals deleted successfully!");
          this.checkedRows = [];
          this.getFestivalList();
        } else {
          // alert(response.data.message || "Failed to delete festivals.");
          this.snackBar.message = response.data.message;
          this.snackBar.show = true;
        } 
      })
      .catch(() => {
        this.$toast && this.$toast.error("Something went wrong!");
      })
      .finally(() => {
        this.loaders.festival_list_loader = false;
      });
    },

    copyFestival(item) {
      const selectedFestivalId = item.edit_festival_id || item.festival_id || item.id || null;

      if (!selectedFestivalId) {
        this.snackBar.message = "Festival ID not found for this row.";
        this.snackBar.color = "error";
        this.snackBar.show = true;
        return;
      }

      this.$emit("copy-festival", {
        ...item,
        festival_id: selectedFestivalId,
      });
    },
  },
};
</script>
